import objectHash from "object-hash";
import { useCallback, useEffect, useMemo } from "react";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import { logError } from "@entrilia/shared/logging.ts";
import { searchDimensionDictionary } from "../../../../../../../../api/biApi.ts";
import type { ConditionField, DimensionDictionary } from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";

interface SelectedDictionaryRowsResult {
  rows: DimensionDictionary[];
  isLoading: boolean;
}

const useSelectedDictionaryRows = (condition: ConditionField): SelectedDictionaryRowsResult => {
  const { dataSetId } = useBiCommonDataContext();
  const selectedValuesHash = objectHash(condition.config.filter?.values ?? []);
  const selectedValues = useMemo(
    () => condition.config.filter?.values ?? [],
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [selectedValuesHash]
  );

  const fetchRows = useCallback(
    (signal?: AbortSignal) =>
      searchDimensionDictionary(
        dataSetId,
        {
          dimensionName: condition.meta.name,
          selectedValuesUserInput: selectedValues,
          allRows: true,
          range: { skip: 0, take: 0, totals: false },
        },
        signal
      ),
    [condition.meta.name, dataSetId, selectedValues]
  );

  const [response, fetchError, { isFetching }] = useFetch(fetchRows, { disabled: selectedValues.length === 0 });
  useEffect(() => {
    if (fetchError !== undefined) {
      logError(fetchError, "[useSelectedDictionaryRows] searchDimensionDictionary");
    }
  }, [fetchError]);

  const selectedRows = useMemo(() => {
    if (selectedValues.length === 0) {
      return [];
    }

    if (response !== undefined) {
      return response.items;
    }

    const selectedValueSet = new Set(selectedValues.map((value) => (value === null ? null : String(value))));
    return condition.meta.dictionary.filter((row) => {
      const value = row[condition.meta.keyFieldName];
      if (value === undefined) {
        return false;
      }
      return selectedValueSet.has(value === null ? null : String(value));
    });
  }, [response, condition.meta.dictionary, condition.meta.keyFieldName, selectedValues]);

  return { rows: selectedRows, isLoading: isFetching };
};

export default useSelectedDictionaryRows;
